/**
 * C.14 / M.2 — NIPE tool runner.
 *
 * Dispatches a single OpenAI-style tool_call against the registry in
 * `./tools/`. M.2 refactor extracted per-tool handlers; this file is
 * now a thin dispatcher.
 *
 * Pure async function — accepts a runner context whose shape is the
 * sum of every per-tool Context (see `NipeToolRunnerContext`).
 *
 * @see ./tools/README.md for the per-tool catalog.
 */

import { ALL_TOOLS, TOOLS_BY_NAME, type NipeToolRunnerContext } from './tools';
import type { NipeToolCall, NipeToolResult } from './tools/types';

/**
 * @deprecated Use `NipeToolCall` from `./tools/types` instead. Kept for
 * backward compatibility with existing callers.
 */
export type ToolCallShape = NipeToolCall;

/**
 * @deprecated Use `NipeToolResult` from `./tools/types` instead. Kept
 * for backward compatibility.
 */
export type ToolResult = NipeToolResult;

/**
 * The runner context. M.2 makes `proposeSharePointDocument` required
 * (all real callers already supply it). `listSharePointDocs` remains
 * optional — some sessions have no SP drive connected.
 */
export type ToolRunnerContext = NipeToolRunnerContext;

export async function runToolCall(
  call: NipeToolCall,
  ctx: NipeToolRunnerContext,
): Promise<NipeToolResult> {
  const name = call.function.name;

  let args: Record<string, unknown> = {};
  try {
    args = call.function.arguments
      ? (JSON.parse(call.function.arguments) as Record<string, unknown>)
      : {};
  } catch {
    args = {};
  }

  const wrap = (data: unknown): NipeToolResult => ({
    toolCallId: call.id,
    name,
    resultJson: JSON.stringify(data),
  });

  const tool = TOOLS_BY_NAME.get(name);
  if (!tool) {
    return wrap({ error: `Unknown tool: ${name}` });
  }

  try {
    const result = await tool.handler(args, ctx);
    return wrap(result);
  } catch (e) {
    return wrap({ error: e instanceof Error ? e.message : String(e) });
  }
}

/** Re-exports for legacy callers. */
export { ALL_TOOLS, TOOLS_BY_NAME };
